import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle,
  Download,
  ExternalLink,
  Heart,
  XCircle,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  Button,
  Input,
  StatusBadge,
  type StatusTone,
  Modal,
  ModalBody,
  ModalFooter,
  NotificationCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { supabase } from '@/shared/lib/supabase';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { formatDate, formatZARFromCents } from '@/shared/utils/format';

/**
 * /admin/care  —  Care Plan subscription manager
 *
 * Phase 10 Slice 10.1, T-10.1-11.
 *
 * Lists every `care_subscription` row across all customers. Status filter
 * (all / active / at_risk / suspended / cancelled) plus aggregated stats
 * (active count, MRR, at_risk count). Each row exposes:
 *   - Customer (email)
 *   - Status badge
 *   - Started + next-billing date
 *   - PayFast subscription token presence (so staff can verify recurring
 *     billing is actually wired)
 *   - "Cancel" button — admin override, calls care-subscription-cancel EF
 *     with staff auth. The EF accepts staff identity and bypasses the
 *     customer-only ownership check (verified in EF source line 127-130).
 *
 * CSV export per AD-27 — exports the currently-filtered rows.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_ARCHITECTURE.md §2.8 (cancel EF)
 *       _bmad-output/PHASE_10_SERVICES_IMPLEMENTATION_PLAN.md T-10.1-11
 */

const CARE_PRICE_CENTS = 19900; // BRD-locked Care Plan monthly price (R199)

interface CareRow {
  id: string;
  service_order_id: string;
  user_id: string;
  status: 'active' | 'at_risk' | 'suspended' | 'cancelled';
  started_at: string;
  next_billing_date: string;
  payfast_subscription_token: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  customer_email: string | null;
}

const STATUS_TONE: Record<CareRow['status'], StatusTone> = {
  active: 'active',
  at_risk: 'pending',
  suspended: 'overdue',
  cancelled: 'failed',
};

type StatusFilter = 'all' | CareRow['status'];

export default function CareSubscriptionsPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<CareRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [cancelTarget, setCancelTarget] = useState<CareRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbErr } = await supabase
        .from('care_subscription')
        .select(
          'id, service_order_id, user_id, status, started_at, next_billing_date, payfast_subscription_token, cancelled_at, cancel_reason',
        )
        .order('started_at', { ascending: false });

      if (dbErr) throw dbErr;

      const userIds = Array.from(new Set((data ?? []).map((r) => (r as { user_id: string }).user_id)));
      const emailMap = new Map<string, string>();
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('customer_profile')
          .select('id, billing_email')
          .in('id', userIds);
        (profiles ?? []).forEach((p) => {
          const row = p as { id: string; billing_email: string | null };
          if (row.billing_email) emailMap.set(row.id, row.billing_email);
        });
      }

      const mapped: CareRow[] = (data ?? []).map((r) => {
        const row = r as Omit<CareRow, 'customer_email'>;
        return { ...row, customer_email: emailMap.get(row.user_id) ?? null };
      });

      setRows(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** STOCK — a subscription roster. `Started`, never "Period". */
  const table = useTableFilters<CareRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Started',
    dateOf: (r) => r.started_at,
    searchText: (r) => [r.customer_email ?? '', r.status, r.cancel_reason ?? ''].join(' '),
    facets: useMemo(
      () => [(r: CareRow) => statusFilter === 'all' || r.status === statusFilter],
      [statusFilter],
    ),
  });

  const filteredRows = table.rows;

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === 'active').length;
    const atRisk = rows.filter((r) => r.status === 'at_risk').length;
    const suspended = rows.filter((r) => r.status === 'suspended').length;
    const cancelled = rows.filter((r) => r.status === 'cancelled').length;
    const mrrCents = (active + atRisk) * CARE_PRICE_CENTS;
    return { active, atRisk, suspended, cancelled, mrrCents };
  }, [rows]);

  const onConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { error: efErr } = await supabase.functions.invoke('care-subscription-cancel', {
        body: {
          care_subscription_id: cancelTarget.id,
          reason: cancelReason.trim() || undefined,
        },
      });
      if (efErr) {
        let msg = efErr.message;
        try {
          const ctx = (efErr as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const parsed = (await ctx.clone().json()) as { code?: string; message?: string };
            if (parsed?.message) msg = parsed.message;
            else if (parsed?.code) msg = parsed.code;
          }
        } catch {
          // fall through
        }
        throw new Error(msg);
      }
      toast({
        tone: 'success',
        title: 'Subscription cancelled',
        body: 'Local row marked cancelled; PayFast cancel attempted. Watch the audit log if PayFast was unreachable.',
      });
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Cancel failed',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setCancelling(false);
    }
  }, [cancelTarget, cancelReason, load, toast]);

  const onExportCsv = useCallback(() => {
    const header = [
      'Subscription ID',
      'Service order ID',
      'Customer email',
      'Customer ID',
      'Status',
      'Started',
      'Next billing date',
      'PayFast token present',
      'Cancelled at',
      'Cancel reason',
    ];
    const dataRows = filteredRows.map((r) => [
      r.id,
      r.service_order_id,
      r.customer_email ?? '',
      r.user_id,
      r.status,
      r.started_at,
      r.next_billing_date,
      r.payfast_subscription_token ? 'yes' : 'no',
      r.cancelled_at ?? '',
      r.cancel_reason ?? '',
    ]);
    const filenameStatus = statusFilter === 'all' ? 'all' : statusFilter;
    exportRowsAsCsv(`care-subscriptions-${filenameStatus}-${todayStamp()}.csv`, header, dataRows);
  }, [filteredRows, statusFilter]);

  const columns = useMemo<UniversalTableColumn<CareRow>[]>(
    () => [
      {
        key: 'customer',
        header: 'Customer',
        render: (r) => (
          <Link
            to={`/admin/customers/${r.user_id}`}
            className="text-sm text-primary hover:underline"
          >
            {r.customer_email ?? r.user_id.slice(0, 8)}
          </Link>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        render: (r) => <StatusBadge tone={STATUS_TONE[r.status]}>{r.status.replace('_', ' ')}</StatusBadge>,
      },
      {
        key: 'started',
        header: 'Started',
        sortValue: (r) => new Date(r.started_at).getTime(),
        render: (r) => <span className="text-sm tabular-nums">{formatDate(r.started_at)}</span>,
        hideOnMobile: true,
      },
      {
        key: 'next_billing',
        header: 'Next billing',
        sortValue: (r) => new Date(r.next_billing_date).getTime(),
        render: (r) => (
          <span
            className={`text-sm tabular-nums ${
              r.status === 'cancelled' ? 'text-muted-foreground line-through' : 'text-foreground'
            }`}
          >
            {formatDate(r.next_billing_date)}
          </span>
        ),
      },
      {
        key: 'mrr',
        header: 'MRR',
        sortValue: (r) => (r.status === 'active' || r.status === 'at_risk' ? CARE_PRICE_CENTS : 0),
        render: (r) =>
          r.status === 'active' || r.status === 'at_risk' ? (
            <span className="text-sm tabular-nums text-foreground">{formatZARFromCents(CARE_PRICE_CENTS)}</span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'token',
        header: 'PayFast',
        render: (r) =>
          r.payfast_subscription_token ? (
            <span className="text-xs text-success">Linked</span>
          ) : (
            <span className="text-xs text-warning-dark">Pending</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'order',
        header: '',
        render: (r) => (
          <Link
            to={`/admin/services/${r.service_order_id}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Order <ExternalLink className="h-3 w-3" />
          </Link>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (r) =>
          r.status === 'cancelled' ? (
            <span className="text-xs text-muted-foreground">Cancelled</span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCancelTarget(r);
                setCancelReason('');
              }}
            >
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </Button>
          ),
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Care subscriptions"
      description="Every Care Plan customer in one place. Filter by status, export to CSV, cancel as admin override."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Services', href: '/admin/services/installment-recovery' },
        { label: 'Care subscriptions' },
      ]}
      actions={
        <Button variant="outline" size="sm" onClick={onExportCsv} disabled={filteredRows.length === 0}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      }
    >
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load Care subscriptions"
          body={error}
        />
      )}

      <Card interactive={false}>
        <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4">
          <Stat label="Active" value={stats.active} />
          <Stat label="At-risk" value={stats.atRisk} tone={stats.atRisk > 0 ? 'amber' : 'default'} />
          <Stat label="MRR" value={formatZARFromCents(stats.mrrCents)} tone="primary" />
          <Stat label="Cancelled (lifetime)" value={stats.cancelled} tone="muted" />
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="at_risk">At-risk</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          Showing {filteredRows.length} of {rows.length}
        </span>
      </div>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <Card interactive={false}>
          <div className="p-8 text-center">
            <Heart className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
            <p className="font-medium text-foreground">
              {statusFilter === 'all'
                ? 'No Care subscriptions yet'
                : `No ${statusFilter.replace('_', ' ')} subscriptions`}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {statusFilter === 'all'
                ? 'When a customer buys the Care Plan, the saga inserts a row here automatically.'
                : 'Adjust the filter to see other statuses.'}
            </p>
          </div>
        </Card>
      ) : (
        <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Started"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search email, status, reason"
            className="ml-auto w-full max-w-xs"
          />
        </div>
        <UniversalTable columns={columns} data={filteredRows} caption={table.caption} />
        </>
      )}

      {/* Cancel confirmation modal */}
      <Modal
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelling) {
            setCancelTarget(null);
            setCancelReason('');
          }
        }}
        title="Cancel Care subscription"
        description={
          cancelTarget
            ? `Customer: ${cancelTarget.customer_email ?? cancelTarget.user_id.slice(0, 8)}`
            : ''
        }
      >
        <ModalBody>
          <NotificationCard
            tone="warning"
            title="This is an admin override"
            body="The customer can self-cancel from /app. Use this only when staff have a clear reason (e.g. customer phone-cancelled, payment dispute). The EF will mark the row cancelled locally and best-effort-call PayFast — if PayFast is unreachable, the audit_log captures the drift for manual cleanup."
          />
          <div className="mt-4 space-y-2">
            <label htmlFor="cancel-reason" className="text-sm font-medium text-foreground">
              Reason (optional, recorded in audit log)
            </label>
            <Input
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Customer requested via support ticket #1234"
              disabled={cancelling}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setCancelTarget(null);
              setCancelReason('');
            }}
            disabled={cancelling}
          >
            Keep subscription
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmCancel}
            disabled={cancelling}
            loading={cancelling}
          >
            <AlertTriangle className="h-4 w-4" /> Cancel subscription
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'primary' | 'amber' | 'muted';
}) {
  const valueClass =
    tone === 'primary'
      ? 'text-primary'
      : tone === 'amber'
        ? 'text-warning-dark'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : 'text-foreground';
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
    </div>
  );
}
